import { useState, lazy, Suspense, createContext, useContext, useEffect } from 'react';
import { Routes, Route, Navigate, /*useNavigate*/ } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import './App.css'
import 'flowbite/dist/flowbite.min.css';
import { IUserInfo } from './services/interfaces/userInfo';
import ScrollToTop from './components/ScrollToTop';
import { ComponentNavbar } from './components/header/ComponentNavbar';
import { PrivateRoute } from './services/utils/PrivateRoute';
import { TrocsRequestsPage } from './pages/trocsRequestsPage/TrocsRequestsPage';
import { TrocPropositionsPage } from './pages/trocPropositionsPage/TrocPropositionsPage';
import MyExchangeElements from './pages/myExchangeElements/MyExchangeElements';
import SearchTrocs from './pages/searchTrocs/SearcTrocs';
import { PageNotFound } from './services/utils/PageNotFound';
import SubscriptionPage from './pages/subscriptionPage/SubscriptionPage';
import FooterGt from './components/footer/FooterGt';
import WhoEnjoyedTrocsPage from './pages/whoEnjoyedTrocsPage/WhoEnjoyedTrocsPage';
import LandingPage from './pages/landingPage/LandingPage';
import CharterOfGoodConduct from './pages/charterOfGoodConduct/CharterOfGoodConduct';
import { Capacitor } from '@capacitor/core';
import MentionLegalPage from './pages/mentionLegalPage/MentionLegalPage';
import MobileBottomNav from './components/header/MobileBottomNav';
import MobileSubscriptionPage from './pages/subscriptionPage/MobileSubscriptionPage';
import NetworkDetector from './components/networkDetector/NetworkDetector';
import { Socket } from 'socket.io-client';
import { initSocket } from './services/callAPI/toChat';
// import { clearTokens, getToken, isTokenExpired } from './services/hooks/storage/tokenStorage';
// import { useDispatch } from 'react-redux';
// import { resetUser } from './store/userSlice';
// import { persistor } from '../src/store';
// import { useApi } from './services/hooks/useApi';

// Lazy load du composant
const PwdForgot = lazy(() => import('./pages/pwdForgotPage/PwdForgot'));
const ResetPasswordPage = lazy(() => import('./pages/resetPasswordPage/ResetPasswordPage'));
const ProfilPage = lazy(() => import('./pages/profilPage/ProfilPage'));
const AboutPage = lazy(() => import('./pages/aboutPage/AboutPage'));
const LegalInfoPage = lazy(() => import('./pages/legalInfoPage/LegalInfoPage'));
const CguPage = lazy(() => import('./pages/cguPage/CguPage'));
const PrivacyPolicy = lazy(() => import('./pages/privacyPolicyPage/PrivacyPolicy'));
const FaqPage = lazy(() => import('./pages/faqPage/FaqPage'));
const ContactPage = lazy(() => import('./pages/contactPage/ContactPage'));

// Créer un contexte react destiné à transporté un objet de type socket venant de socket.io-client
const SocketContext = createContext<Socket | null>(null);

// fonction qui permet de consommer ce contexte facilement dans n’importe quel composant enfant de TrocsPropositionsTable — au lieu de faire useContext(SocketContext) à chaque fois.
export const useSocket = () => useContext(SocketContext);

function App() {
  const userRol = useSelector((state: RootState) => state.user.role);
  const notifNbr = useSelector((state: RootState) => state.user.notifNumber);
  const isConnected = useSelector((state: RootState) => state.user.isConnect);
  const pseudo = useSelector((state: RootState) => state.user.pseudo);
  const badges = useSelector((state: RootState) => state.user.badge);

  const [userInfo, _setUserInfo] = useState<IUserInfo | null>({ role: userRol, notifNumber: notifNbr, pseudo, badges: badges });

  const [isShowHeader, setIsShowHeader] = useState<boolean>(isConnected);

  const [isConnect, setIsConnect] = useState<boolean>(isConnected);

  const [socket, setSocket] = useState<Socket | null>(null); // Stocker le socket ici

  const plateForm = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  // const dispatch = useDispatch();

  // const navigate = useNavigate();

  function handleIsShowHeader() {
    setIsShowHeader(true);
  }

  //useEffect to initialiser la connexion via la librairie socket.io. pour faciliter l'implémentation du protocole WebSocket === protocole temps réel bidirectionnel
  useEffect(() => {
    if (userInfo?.role && !socket) {

      //init socket
      const handleInitSocket = async () => {
        try {
          const socketInstance = await initSocket();

          if (socketInstance) {
            setSocket(socketInstance);
          }
          else {
            console.warn("Socket non initialisé");
          }
        }
        catch (error) {
          null;
        }
      }
      handleInitSocket();
    }
    //sinon don't init socket
  }, [userInfo]);

  // if (plateForm !== "web") {
  //   useEffect(() => {
  //     const checkTokenExpired = async () => {
  //       const token = await getToken();
  //       if (token && !isTokenExpired(token)) {
  //         setIsConnect(true);
  //         setIsShowHeader(true);
  //       } else {
  //         await clearTokens();

  //         //reset redux's user state
  //         dispatch(resetUser());

  //         //delete data into localstorage stock by redux persistor
  //         persistor.purge();

  //         setIsConnect(false);

  //         setIsShowHeader(false);

  //         navigate('/');
  //       }
  //     }
  //     checkTokenExpired();
  //   }, [])
  // }

  return (
    <>
      <SocketContext.Provider value={socket}>
        <NetworkDetector />

        {import.meta.env.DEV && <p>⚡ Mode Développement activé</p>}
        <ScrollToTop /> {/* Remonte après chaque changement de route */}

        {/* commons components to all pages and specifics components about pages*/}
        {isShowHeader &&
          <header>
            <ComponentNavbar setIsShowHeader={setIsShowHeader} userInfo={userInfo} setIsConnect={setIsConnect} />
            <MobileBottomNav userInfo={userInfo} />
          </header>
        }

        <main className='flex-1'>
          <Routes>
            {/* router when user not connected or connected */}
            {!isConnect ? (
              <Route path='/' element={<LandingPage />} />
            ) : userInfo?.role === "TRAVELER" ? (
              <Route path='/' element={<Navigate to="/trade_requests" replace />} />
            ) : userInfo?.role === "RESIDENT" && (
              <Route path='/' element={<Navigate to="/trade_propositions" replace />} />
            )}

            {/* router when user forget pwd */}
            {!isConnect ? (
              <Route
                path='/password-forgot'
                element={
                  <Suspense fallback={<div>Chargement...</div>}>
                    <PwdForgot />
                  </Suspense>
                }
              />
            ) : null}

            {!isConnect ? (
              <Route
                path='/ask-reset-password'
                element={
                  <Suspense fallback={<div>Chargement...</div>}>
                    <ResetPasswordPage />
                  </Suspense>
                }
              />
            ) : null}

            {/* router TRAVELER */}
            {
              userInfo?.role === "TRAVELER" && (
                <Route element={<PrivateRoute handleIsShowHeader={handleIsShowHeader} />}>
                  <Route path='/trade_requests' element={<TrocsRequestsPage />} />
                  <Route path='/my_trocs' element={<MyExchangeElements />} />
                  <Route path='/search_trocs' element={<SearchTrocs />} />
                  <Route
                    path='/profil'
                    element={
                      <Suspense fallback={<div>Chargment...</div>}>
                        <ProfilPage />
                      </Suspense>
                    }
                  />
                  <Route path='/manage-my-subscription' element={isNative ? <MobileSubscriptionPage /> : <SubscriptionPage />} />
                  <Route path='/who-enjoyed-the-troc/:trocIdTrav' element={<WhoEnjoyedTrocsPage />} />
                  <Route path='*' element={<PageNotFound />} />
                </Route>
              )
            }

            {/*router RESIDENT */}
            {
              userInfo?.role === "RESIDENT" && (
                <Route element={<PrivateRoute handleIsShowHeader={handleIsShowHeader} />}>
                  <Route path='/trade_propositions' element={<TrocPropositionsPage />} />
                  <Route path='/my_trocs' element={<MyExchangeElements />} />
                  <Route
                    path='/profil'
                    element={
                      <Suspense fallback={<div>Chargment...</div>}>
                        <ProfilPage />
                      </Suspense>
                    }
                  />
                  <Route path='/manage-my-subscription' element={isNative ? <MobileSubscriptionPage /> : <SubscriptionPage />} />
                  <Route path='/search_trocs' element={<SearchTrocs />} />
                  <Route path='*' element={<PageNotFound />} />
                </Route>
              )
            }

            {/* router footer */}
            <Route
              path='/about'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <AboutPage isConnect={isConnect} />
                </Suspense>
              }
            />
            <Route
              path='/info-legal'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <LegalInfoPage isConnect={isConnect} />
                </Suspense>
              }
            />
            <Route
              path='/terms-services'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <CguPage isConnect={isConnect} />
                </Suspense>
              }
            />
            <Route
              path='/privacy-policy'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <PrivacyPolicy isConnect={isConnect} />
                </Suspense>
              }
            />
            <Route
              path='/charter-of-good-conduct'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <CharterOfGoodConduct isConnect={isConnect} />
                </Suspense>
              }
            />
            <Route
              path='/faq'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <FaqPage isConnect={isConnect} />
                </Suspense>
              }
            />
            <Route
              path='/contact'
              element={
                <Suspense fallback={<div>Chargement...</div>}>
                  <ContactPage isConnect={isConnect} />
                </Suspense>
              }
            />

            {isConnect &&
              <Route
                path='/mentions-legales'
                element={
                  <Suspense fallback={<div>Chargement...</div>}>
                    <MentionLegalPage />
                  </Suspense>
                }
              />
            }
            <Route path='*' element={<PageNotFound />} />
          </Routes>

        </main>

        {!(isConnect && (plateForm === "web" || plateForm === "android" || plateForm === "ios")) && <FooterGt />}
      </SocketContext.Provider>
    </>
  )
}

export default App;
